import Label from '@/components/elements/Label/Label';
import MerchantLogo from '@/components/elements/MerchantLogo/MerchantLogo';
import Rating from '@/components/elements/redesign/Rating/Rating';
import { classnames, getImageSize } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { forwardRef, ReactNode, SyntheticEvent, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BrandProductImage } from '../BrandProductImage';
import { getFooterSlots } from './helpers';
import { MPServiceCardFooter } from './shared';
import { MPServiceCardInnerProps, MPServiceCardProps } from './types';

export const CARD_HEIGHT = 380;

const CARD_CONTAINER_CLASSNAMES = classnames(
  /**
   * Base styles
   */
  'bg-surfaceColors-surface_l2 w-full overflow-hidden rounded-lg transition-border duration-150',
  'border-transparent border',
  'shadow-xs',
  /**
   * Interactive styles
   */
  'hover:border-border_regular active:border-border_bold',
  'hover:shadow-md active:shadow-none',
);

const TITLE_CLASSNAMES = 'text-fg_primary line-clamp-[2] overflow-hidden text-ellipsis text-xl font-bold';
const SECONDARY_TEXT_CLASSNAMES = 'text-fg_secondary text-m line-clamp-[1]';

/** Secondary text (merchant name or address) on the left, rating on the right. */
const rowWithRating = ({
  text,
  reviewRating,
  ratingCount,
}: {
  text?: string;
  reviewRating?: number;
  ratingCount?: number;
}) => {
  if (!text && !(reviewRating > 0)) return null;
  return (
    <div className="gap-xs flex w-full items-center justify-between">
      {text && <span className={SECONDARY_TEXT_CLASSNAMES}>{text}</span>}
      {reviewRating > 0 && (
        <Rating
          showOneStar
          ratingCount={ratingCount}
          size="sm"
          hideCountLabel
          avgRating={reviewRating}
          className="ml-auto flex-shrink-0"
        />
      )}
    </div>
  );
};

const getContentSlots = (props: MPServiceCardInnerProps): { upper: ReactNode; title: ReactNode; lower: ReactNode } => {
  /**
   * Deals layout:
   * - Upper: merchant name + rating (if available)
   * - Title: service name
   * - Lower: address (if available)
   *
   * Sistaminuten layout (unchanged):
   * - Title: service name
   * - Lower: address (subtitle) + rating
   */

  return {
    upper:
      props.type === 'deal' ? (
        rowWithRating({ text: props.subtitle, reviewRating: props.reviewRating, ratingCount: props.ratingCount })
      ) : props.subtitle ? (
        <span className={SECONDARY_TEXT_CLASSNAMES}>{props.subtitle}</span>
      ) : null,
    title: <h3 className={TITLE_CLASSNAMES}>{props.title}</h3>,
    lower:
      props.type === 'deal' ? (
        props.address ? (
          <span className={classnames(SECONDARY_TEXT_CLASSNAMES, 'block')}>{props.address}</span>
        ) : null
      ) : (
        rowWithRating({ text: props.address, reviewRating: props.reviewRating, ratingCount: props.ratingCount })
      ),
  };
};

const MPServiceCardContent = ({ upper, title, lower }: ReturnType<typeof getContentSlots>) => (
  <div className="space-y-sm">
    {upper}
    {title}
    {lower}
  </div>
);

const MPServiceCardInner = forwardRef<HTMLDivElement, MPServiceCardInnerProps>((props, ref) => {
  const { isInLinkWrapper = false, imageUrl, onClick, labels, merchantLogoUrl } = props;
  const { isMobileView } = useMobileView();

  const optimizedImageUrl = useMemo(() => {
    if (!imageUrl) return null;
    return getImageSize(imageUrl, isMobileView ? 'big' : 'medium_l');
  }, [imageUrl, isMobileView]);

  const handleImageError = useCallback(
    (e: SyntheticEvent<HTMLImageElement, Event>) => {
      const fallbackDimensions = isMobileView ? '300x200' : '390x260';
      e.currentTarget.src = `/images/bd-fallback-img${fallbackDimensions}.jpg`;
    },
    [isMobileView],
  );

  return (
    <div
      ref={ref}
      onClick={onClick}
      className={classnames(
        !isInLinkWrapper && CARD_CONTAINER_CLASSNAMES,
        'flex w-full flex-col',
        onClick && 'cursor-pointer',
      )}
      style={{ height: CARD_HEIGHT }}>
      {/* Card Image Header */}
      <div className="relative flex-1">
        <img
          className="absolute inset-0 h-full w-full object-cover object-center"
          alt=""
          loading="lazy"
          src={optimizedImageUrl}
          onError={handleImageError}
        />

        {/* Overlay: labels and logos */}
        <div className="absolute inset-0">
          {labels && (
            <div className="p-md space-x-xs no-scrollbar rounded-xs flex flex-row items-center overflow-x-scroll">
              {labels.map((labelProps, index) => (
                <Label key={index} className="whitespace-nowrap" {...labelProps} />
              ))}
            </div>
          )}

          <div className="bottom-md left-md absolute">
            <MerchantLogo imgUrl={merchantLogoUrl} alt="" size="sm" />
          </div>

          {props.type === 'deal' && props.brandProductImageUrl && (
            <div className="bottom-md right-md absolute">
              <BrandProductImage size="sm" imgSrc={props.brandProductImageUrl} />
            </div>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="p-md space-y-md">
        <MPServiceCardContent {...getContentSlots(props)} />
        <MPServiceCardFooter {...getFooterSlots(props)} />
      </div>
    </div>
  );
});

const CARD_OUTER_CLASSNAMES = classnames(CARD_CONTAINER_CLASSNAMES, 'flex w-full flex-col');

/**
 * Link Wrapper if "to" prop is provided
 */
const MPServiceCard = forwardRef<HTMLDivElement, MPServiceCardProps>((props, ref) => {
  const { to, onClick, ...rest } = props;

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={classnames('inline-block', CARD_CONTAINER_CLASSNAMES)}>
        <MPServiceCardInner ref={ref} {...rest} isInLinkWrapper />
      </Link>
    );
  }

  return <MPServiceCardInner ref={ref} onClick={onClick} {...rest} />;
});

export default MPServiceCard;

const SKELETON_CLASS = 'bg-surface_l4 animate-pulse rounded';

export const MPServiceCardSkeleton = ({ type = 'deal' }: { type?: MPServiceCardProps['type'] }) => (
  <div aria-hidden="true" className={CARD_OUTER_CLASSNAMES} style={{ height: CARD_HEIGHT }}>
    {/* Image area */}
    <div className="bg-surface_l4 relative flex-1 animate-pulse">
      {/* Labels row */}
      <div className="p-md space-x-xs flex flex-row items-center">
        <div className={classnames(SKELETON_CLASS, 'h-lg w-16')} />
        <div className={classnames(SKELETON_CLASS, 'h-lg w-12')} />
      </div>
      {/* Merchant logo */}
      <div className="bottom-md left-md absolute">
        <div className={classnames(SKELETON_CLASS, 'rounded-full')} style={{ width: 48, height: 48 }} />
      </div>
    </div>

    {/* Content area */}
    <div className="p-md space-y-md">
      <div className="space-y-sm">
        {type === 'deal' ? (
          <>
            {/* Deal layout: merchant name + rating row → title → address */}
            <div className="gap-xs flex w-full items-center justify-between">
              <div className={classnames(SKELETON_CLASS, 'h-md w-1/3')} />
              <div className={classnames(SKELETON_CLASS, 'h-md w-12')} />
            </div>
            <div className={classnames(SKELETON_CLASS, 'h-lg w-3/4')} />
            <div className={classnames(SKELETON_CLASS, 'h-md w-1/2')} />
          </>
        ) : (
          <>
            {/* Sistaminuten layout: title → address + rating row */}
            <div className={classnames(SKELETON_CLASS, 'h-lg w-3/4')} />
            <div className="gap-xs flex w-full items-center justify-between">
              <div className={classnames(SKELETON_CLASS, 'h-md w-1/2')} />
              <div className={classnames(SKELETON_CLASS, 'h-md w-12')} />
            </div>
          </>
        )}
      </div>

      {/* Footer (matches grid layout of actual card) */}
      <div className="pt-md border-border_regular gap-x-sm gap-y-2xs grid grid-cols-[1fr_auto] border-t">
        <div className="col-start-1 row-start-1">
          <div className={classnames(SKELETON_CLASS, 'h-lg w-20')} />
        </div>
        <div className="col-start-1 row-start-2">
          <div className={classnames(SKELETON_CLASS, 'h-md w-16')} />
        </div>
        <div className="col-start-2 row-span-2 row-start-1 self-center">
          <div className={classnames(SKELETON_CLASS, 'h-lg w-20')} />
        </div>
      </div>
    </div>
  </div>
);
